import { useEffect, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mountain, Smartphone, PhoneCall, CalendarCheck, MapPin, Zap, CheckCircle2,
  Loader2, ArrowRight, Menu, X, Mail, Phone, Star, LayoutDashboard,
  MailCheck, MousePointerClick, XCircle, Search,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const IMG = {
  trade: "https://images.unsplash.com/photo-1608613304899-ea8098577e38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjb250cmFjdG9yJTIwd29ya2VyJTIwY29uc3RydWN0aW9uJTIwd29ya3Nob3AlMjB0b29sJTIwY3JhZnRzbWFufGVufDB8fHx8MTc5MTQ4ODcyNHww&ixlib=rb-4.1.0&q=85",
  workspace: "https://images.unsplash.com/photo-1770816307528-67e404552dcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsb2NhbCUyMGJ1c2luZXNzJTIwbW9kZXJuJTIwd29ya3Nob3AlMjBpbnRlcmlvciUyMHdhcm0lMjBsaWdodGluZ3xlbnwwfHx8MTc5MTQ4NzQ3Mnww&ixlib=rb-4.1.0&q=85",
  tools: "https://images.unsplash.com/photo-1497218770144-3fea6dbc33fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMzl8MHwxfHNlYXJjaHwzfHxjcmFmdHNtYW5zaGlwJTIwZGV0YWlsJTIwd29vZHdvcmtpbmclMjBhcmNoaXRlY3R1cmUlMjBwcmVjaXNpb24lMjB0b29sfGVufDB8fHx8MTc5MTQ4ODcyNHww&ixlib=rb-4.1.0&q=85",
  craftsman: "https://images.unsplash.com/photo-1631396326838-de37e5f8bcbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMzl8MHwxfHNlYXJjaHwxfHxjcmFmdHNtYW5zaGlwJTIwZGV0YWlsJTIwd29vZHdvcmtpbmclMjBhcmNoaXRlY3R1cmUlMjBwcmVjaXNpb24lMjB0b29sfGVufDB8fHx8MTc5MTQ4ODcyNHww&ixlib=rb-4.1.0&q=85",
};

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.7, ease: EASE } }),
};

const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) return;
  if (window.__rmw_lenis) window.__rmw_lenis.scrollTo(el, { offset: -72, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const Overline = ({ children }) => (
  <p className="font-mono-accent text-xs uppercase tracking-[0.22em] text-[#F59E0B] mb-5">{children}</p>
);

function Header() {
  const [open, setOpen] = useState(false);
  const links = [
    ["The problem", "#problem", "nav-link-problem"],
    ["Services", "#services", "nav-link-services"],
    ["Pricing", "#pricing", "nav-link-pricing"],
    ["Case study", "#case-study", "nav-link-portfolio"],
    ["Process", "#process", "nav-link-process"],
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0C0E]/80 border-b border-[#272B35]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <button data-testid="nav-brand-logo" onClick={() => window.__rmw_lenis?.scrollTo(0)} className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D97706] to-[#EA580C] text-white flex items-center justify-center">
            <Mountain size={18} strokeWidth={2.2} />
          </span>
          <span className="leading-none text-left">
            <span className="font-display font-bold text-[#FDFBF7] text-base block tracking-tight">Rocky Mountain</span>
            <span className="font-mono-accent text-[9px] uppercase tracking-[0.28em] text-[#F59E0B] block mt-1">Websites</span>
          </span>
        </button>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map(([label, id, tid]) => (
            <button key={tid} data-testid={tid} onClick={() => scrollToId(id)}
              className="text-sm text-[#9CA3AF] hover:text-[#FDFBF7] transition-colors duration-200">
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button data-testid="nav-cta-video-audit" onClick={() => scrollToId("#audit")}
            className="hidden lg:inline-flex items-center gap-1.5 bg-gradient-to-r from-[#D97706] to-[#EA580C] hover:opacity-90 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-opacity duration-200">
            Free video audit <ArrowRight size={15} />
          </button>
          <button data-testid="nav-mobile-menu-button" onClick={() => setOpen(!open)} className="lg:hidden text-[#FDFBF7] p-2" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-[#272B35] bg-[#0B0C0E] overflow-hidden" data-testid="nav-mobile-menu">
            <div className="px-5 py-4 space-y-1">
              {links.map(([label, id, tid]) => (
                <button key={tid} data-testid={`mobile-${tid}`} onClick={() => { setOpen(false); scrollToId(id); }}
                  className="block w-full text-left py-2.5 text-[#D1D5DB] text-sm">
                  {label}
                </button>
              ))}
              <button data-testid="mobile-nav-cta-video-audit" onClick={() => { setOpen(false); scrollToId("#audit"); }}
                className="w-full mt-2 bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white text-sm font-semibold px-5 py-3 rounded-full">
                Free video audit
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function PhoneMockup() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9, ease: EASE }}
      className="relative mx-auto w-[270px] sm:w-[300px]"
      data-testid="hero-phone-preview-card"
    >
      <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="rounded-[2.6rem] border border-[#272B35] bg-[#14161A] p-2.5 shadow-2xl shadow-black/60">
        <div className="rounded-[2rem] bg-[#0B0C0E] overflow-hidden">
          <div className="relative h-40">
            <img src={IMG.trade} alt="Craftsman at work" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute bottom-3 left-4">
              <p className="font-serif-accent italic text-[#FDFBF7] text-xl">Summit Peak Roofing</p>
              <p className="font-mono-accent text-[9px] uppercase tracking-[0.24em] text-[#F59E0B]">Licensed · Local · 4.9 on Google</p>
            </div>
          </div>
          <div className="p-4">
            <div className="flex gap-1.5 mb-3">
              {["Repairs", "Replacement", "Inspections"].map((t, i) => (
                <span key={t} className={`text-[10px] font-semibold px-3 py-1.5 rounded-full ${i === 0 ? "bg-[#D97706] text-white" : "bg-[#1C1F26] text-[#9CA3AF]"}`}>{t}</span>
              ))}
            </div>
            {["Storm & leak repair — same week", "Free 21-point roof inspection", "Insurance claim assistance"].map((s) => (
              <div key={s} className="flex items-center gap-2 py-2 border-b border-[#1C1F26] last:border-0">
                <CheckCircle2 size={12} className="text-[#F59E0B] shrink-0" />
                <span className="text-[11px] text-[#D1D5DB]">{s}</span>
              </div>
            ))}
            <div className="mt-3.5 space-y-2">
              <span className="block text-center bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white text-xs font-bold py-2.5 rounded-xl">Get a free quote</span>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-center border border-[#272B35] text-[#D1D5DB] text-[10px] font-semibold py-2 rounded-xl">Call</span>
                <span className="text-center border border-[#272B35] text-[#D1D5DB] text-[10px] font-semibold py-2 rounded-xl">Directions</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <p className="text-center font-mono-accent text-[10px] text-[#6B7280] mt-4 tracking-wider">DEMO MOCKUP — WHAT YOUR CUSTOMERS SEE</p>
    </motion.div>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-[#D97706]/10 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={0}
            className="inline-block font-mono-accent text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#F59E0B] border border-[#D97706]/40 rounded-full px-4 py-2 mb-7"
            data-testid="hero-niche-badge">
            For local businesses that live on reputation
          </motion.p>
          <motion.h1 variants={reveal} initial="hidden" animate="show" custom={1}
            className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] leading-[1.08] mb-6"
            data-testid="hero-main-headline">
            Turn "near me" searches into <span className="font-serif-accent italic text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#EA580C]">ringing phones.</span>
          </motion.h1>
          <motion.p variants={reveal} initial="hidden" animate="show" custom={2}
            className="text-base sm:text-lg text-[#9CA3AF] leading-relaxed max-w-xl mb-9" data-testid="hero-subheadline">
            Before customers call, they check your website on their phone. We build fast,
            mobile-first websites for local businesses that turn lookers into callers,
            quote requests, and booked jobs — without you touching a line of code.
          </motion.p>
          <motion.div variants={reveal} initial="hidden" animate="show" custom={3} className="flex flex-wrap gap-4 mb-10">
            <button data-testid="hero-primary-audit-cta" onClick={() => scrollToId("#audit")}
              className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white font-semibold px-7 py-4 rounded-full shadow-lg shadow-[#D97706]/25 hover:shadow-[#D97706]/40 transition-shadow duration-300">
              Claim your free 2-min video audit
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button data-testid="hero-secondary-portfolio-cta" onClick={() => scrollToId("#case-study")}
              className="inline-flex items-center gap-2 border border-[#272B35] text-[#D1D5DB] hover:border-[#D97706]/60 hover:text-[#FDFBF7] font-semibold px-7 py-4 rounded-full transition-colors duration-300">
              See what we build
            </button>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" animate="show" custom={4} className="flex flex-wrap gap-x-7 gap-y-3">
            {[[Zap, "Sub-second load speeds"], [Search, "Google Business Profile ready"], [Star, "Built to earn 5-star reviews"]].map(([Icon, t]) => (
              <span key={t} className="flex items-center gap-2 text-xs text-[#9CA3AF]">
                <Icon size={14} className="text-[#F59E0B]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>
        <PhoneMockup />
      </div>
    </section>
  );
}

function Problem() {
  const pains = [
    "A site built years ago that falls apart on a phone screen",
    "Phone number buried — no tap-to-call anywhere in sight",
    "Slow pages that lose impatient searchers before they load",
    "Looks abandoned, so customers assume the business is too",
  ];
  const wins = [
    "Tap-to-call and a quote form on every screen",
    "Loads in under a second, even on bad cellular",
    "Your best reviews front and center, building instant trust",
    "Always current — we handle every update for you",
  ];
  return (
    <section id="problem" className="py-24 md:py-32 scroll-mt-20" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Overline>The silent leak</Overline>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] max-w-2xl leading-[1.15] mb-4" data-testid="problem-section-title">
            Your work is excellent. Your website is quietly sending people to competitors.
          </h2>
          <p className="text-[#9CA3AF] max-w-2xl leading-relaxed mb-14">
            Every day, people nearby search for exactly what you do, tap your site, and leave.
            You never meet them — you just wonder why the phone isn't ringing.
          </p>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={0}
            className="rounded-3xl border border-[#272B35] bg-[#14161A] p-8" data-testid="problem-card-pdf-menus">
            <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-6">The usual local business site</p>
            <ul className="space-y-4">
              {pains.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-[#9CA3AF] leading-relaxed">
                  <XCircle size={17} className="text-red-400/70 mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={1}
            className="rounded-3xl border border-[#D97706]/40 bg-[#14161A] p-8 relative overflow-hidden" data-testid="solution-card-mobile-menus">
            <img src={IMG.workspace} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
            <div className="relative">
              <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F59E0B] mb-6">The Rocky Mountain build</p>
              <ul className="space-y-4">
                {wins.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-sm text-[#F3F4F6] leading-relaxed">
                    <CheckCircle2 size={17} className="text-[#34D399] mt-0.5 shrink-0" /> {w}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Services() {
  const services = [
    { Icon: Smartphone, title: "Mobile-first design", desc: "Crafted for the phone screen — where most of your customers first meet your business.", tid: "service-card-mobile-first", span: "md:col-span-3" },
    { Icon: PhoneCall, title: "Tap-to-call & quote forms", desc: "One tap to call, text, or request a quote — from every page, at every scroll depth.", tid: "service-card-touch-menu", span: "md:col-span-3" },
    { Icon: MapPin, title: "Maps & reviews built in", desc: "One-tap directions and your Google rating placed exactly where it builds trust.", tid: "service-card-google-maps", span: "md:col-span-2" },
    { Icon: Zap, title: "Sub-second loading", desc: "Optimized code and compressed photos so your site appears instantly on cellular.", tid: "service-card-fast-loading", span: "md:col-span-2" },
    { Icon: Search, title: "Found on Google", desc: "Local SEO setup and Google Business Profile alignment so 'near me' searches find you.", tid: "service-card-ordering-integration", span: "md:col-span-2" },
  ];
  return (
    <section id="services" className="py-24 md:py-32 scroll-mt-20" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Overline>What you get</Overline>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] max-w-2xl leading-[1.15] mb-14">
            Built for how local customers actually decide.
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-5" data-testid="services-bento-grid">
          {services.map(({ Icon, title, desc, tid, span }, i) => (
            <motion.div key={tid} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i}
              className={`${span} rounded-3xl border border-[#272B35] bg-[#14161A] p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#D97706]/50`}
              data-testid={tid}>
              <span className="w-11 h-11 rounded-xl bg-[#D97706]/15 text-[#F59E0B] flex items-center justify-center mb-5">
                <Icon size={20} />
              </span>
              <h3 className="font-display font-semibold text-lg text-[#F3F4F6] mb-2.5">{title}</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const setup = ["Custom mobile-first website for your trade", "Services, photos & quote form setup", "Tap-to-call, texting & map integration", "Google Business Profile & local SEO setup", "Lead capture wired straight to your phone"];
  const care = ["High-speed cloud hosting included", "Unlimited content & price updates", "Daily backups & security", "Priority support — text or call", "Ongoing speed & SEO tune-ups"];
  return (
    <section id="pricing" className="py-24 md:py-32 scroll-mt-20" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Overline>Straight pricing, no surprises</Overline>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] max-w-2xl leading-[1.15] mb-14">
            One great site. One simple monthly care plan.
          </h2>
        </motion.div>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="rounded-[2rem] border border-[#D97706]/40 bg-[#14161A] overflow-hidden" data-testid="pricing-featured-card">
          <div className="grid lg:grid-cols-2">
            <div className="p-9 md:p-12 border-b lg:border-b-0 lg:border-r border-[#272B35]">
              <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">The build</p>
              <p className="font-display font-bold text-5xl text-[#FDFBF7] mb-1.5" data-testid="pricing-setup-amount">$1,500</p>
              <p className="text-sm text-[#9CA3AF] mb-8">one-time · most sites live in about two weeks</p>
              <ul className="space-y-3.5">
                {setup.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-sm text-[#D1D5DB]">
                    <CheckCircle2 size={16} className="text-[#F59E0B] mt-0.5 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-9 md:p-12 bg-[#1C1F26]/60">
              <p className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F59E0B] mb-3">The care plan</p>
              <p className="font-display font-bold text-5xl text-[#FDFBF7] mb-1.5" data-testid="pricing-care-amount">$75<span className="text-xl text-[#9CA3AF] font-semibold">/mo</span></p>
              <p className="text-sm text-[#9CA3AF] mb-8">cancel anytime · you own your site</p>
              <ul className="space-y-3.5 mb-9">
                {care.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-[#D1D5DB]">
                    <CheckCircle2 size={16} className="text-[#34D399] mt-0.5 shrink-0" /> {c}
                  </li>
                ))}
              </ul>
              <button data-testid="pricing-get-started-cta" onClick={() => scrollToId("#audit")}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white font-semibold py-4 rounded-full transition-opacity duration-200 hover:opacity-90">
                Start with a free video audit <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CaseStudy() {
  const tabs = [
    { id: "landing", label: "Lead capture page", Icon: MousePointerClick, tid: "portfolio-tab-landing-page",
      title: "A page built to capture, not just impress",
      copy: "Every visitor sees one clear next step. Forms feed straight into a lead list with instant owner alerts — no enquiry ever slips through." },
    { id: "email", label: "Automated follow-up", Icon: MailCheck, tid: "portfolio-tab-automated-email",
      title: "Follow-ups that send themselves",
      copy: "New enquiries get an immediate personal confirmation, then a timed email sequence over the following week — written once, working forever." },
    { id: "dashboard", label: "Owner dashboard", Icon: LayoutDashboard, tid: "portfolio-tab-analytics-dashboard",
      title: "Know exactly what your site earns you",
      copy: "A private dashboard shows visits, where they came from, and every lead — so you see which posts and ads actually make the phone ring." },
  ];
  const [active, setActive] = useState("landing");
  const current = tabs.find((t) => t.id === active);
  return (
    <section id="case-study" className="py-24 md:py-32 scroll-mt-20" data-testid="portfolio-case-study-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Overline>Real work, running right now</Overline>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] max-w-2xl leading-[1.15] mb-4">
            Not mockups. A live system we designed, built, and operate.
          </h2>
          <p className="text-[#9CA3AF] max-w-2xl leading-relaxed mb-12">
            This exact stack — capture page, automated follow-up, analytics dashboard — is what
            your site is built on. Here's what's under the hood:
          </p>
        </motion.div>
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="rounded-[2rem] border border-[#272B35] bg-[#14161A] overflow-hidden grid lg:grid-cols-5">
          <div className="lg:col-span-3 p-9 md:p-12">
            <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-tabs">
              {tabs.map(({ id, label, Icon, tid }) => (
                <button key={id} data-testid={tid} onClick={() => setActive(id)}
                  className={`relative flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full transition-colors duration-200 ${
                    active === id ? "text-white" : "text-[#9CA3AF] hover:text-[#F3F4F6] bg-[#1C1F26]"
                  }`}>
                  {active === id && <motion.span layoutId="rmw-tab" className="absolute inset-0 bg-gradient-to-r from-[#D97706] to-[#EA580C] rounded-full" />}
                  <Icon size={14} className="relative" /> <span className="relative">{label}</span>
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35, ease: EASE }}
                data-testid="portfolio-tab-content">
                <h3 className="font-display font-semibold text-xl sm:text-2xl text-[#F3F4F6] mb-3">{current.title}</h3>
                <p className="text-sm sm:text-base text-[#9CA3AF] leading-relaxed max-w-lg">{current.copy}</p>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="lg:col-span-2 relative min-h-[260px]">
            <img src={IMG.craftsman} alt="Craftsman inspecting his work" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E]/80 to-transparent" />
            <p className="absolute bottom-5 left-6 font-serif-accent italic text-[#FDFBF7] text-lg">Your craft deserves a site this careful.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "01", title: "Free 2-min video audit", desc: "Send your current site. We record a personal video showing exactly what's costing you jobs.", tid: "process-step-1-audit" },
    { n: "02", title: "Custom design & integration", desc: "We build your mobile-first site, structure your services, and connect calls, texts, and quote forms.", tid: "process-step-2-design" },
    { n: "03", title: "Launch & care plan", desc: "Your site goes live. We host it, update your content whenever you ask, and keep it fast.", tid: "process-step-3-launch" },
  ];
  return (
    <section id="process" className="py-24 md:py-32 scroll-mt-20" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Overline>Fifteen minutes of your time, total</Overline>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] max-w-2xl leading-[1.15] mb-14">
            You run the business. We run the website.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5">
          {steps.map(({ n, title, desc, tid }, i) => (
            <motion.div key={tid} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i}
              className="rounded-3xl border border-[#272B35] bg-[#14161A] p-8" data-testid={tid}>
              <p className="font-mono-accent text-4xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#EA580C] mb-5">{n}</p>
              <h3 className="font-display font-semibold text-lg text-[#F3F4F6] mb-2.5">{title}</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AuditForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: "website",
        message: `Free video audit — Business: ${form.business.trim()} | Current site: ${form.website.trim() || "none given"}`,
        consent: true,
      });
      setDone(true);
      toast.success("Request received — your audit is in the queue.");
      window.__rmw_track?.("audit_submit");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-[#272B35] bg-[#0B0C0E] px-4 py-3.5 text-[#FDFBF7] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#D97706]/40 focus:border-[#D97706] transition-colors duration-200";

  return (
    <section id="audit" className="py-24 md:py-32 scroll-mt-20" data-testid="audit-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="rounded-[2.5rem] border border-[#272B35] bg-[#14161A] overflow-hidden grid lg:grid-cols-2">
          <div className="relative p-9 md:p-14 flex flex-col justify-center min-h-[340px]">
            <img src={IMG.tools} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#14161A]/60 to-[#14161A]" />
            <div className="relative">
              <Overline>Free — no strings</Overline>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-[1.15] mb-5">
                Get a free 2-minute video audit of your current website.
              </h2>
              <p className="text-[#9CA3AF] leading-relaxed max-w-md mb-8">
                We record a personal walkthrough of your site on a phone — what's costing you
                customers and the first three things we'd fix. Yours to keep, whether you hire us or not.
              </p>
              <ul className="space-y-3 text-sm text-[#D1D5DB]">
                {["Delivered within one business day", "Specific to YOUR site — not a template pitch", "No obligation, no pushy sales calls"].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-[#F59E0B] mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="p-9 md:p-14 bg-[#1C1F26]/50">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: EASE }}
                  className="h-full min-h-[340px] flex flex-col items-center justify-center text-center" data-testid="audit-form-success">
                  <span className="w-16 h-16 rounded-full bg-[#059669]/15 text-[#34D399] flex items-center justify-center mb-6">
                    <CheckCircle2 size={32} />
                  </span>
                  <h3 className="font-display font-bold text-2xl text-[#FDFBF7] mb-3">You're in the queue, {form.name.split(" ")[0]}.</h3>
                  <p className="text-[#9CA3AF] max-w-xs leading-relaxed">
                    We'll record your audit of {form.business || "your business"} and email it within one business day.
                  </p>
                </motion.div>
              ) : (
                <motion.form key="form" exit={{ opacity: 0 }} onSubmit={submit} className="space-y-4" data-testid="audit-form">
                  <input data-testid="audit-form-name-input" required minLength={2} placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="audit-form-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="audit-form-phone-input" required minLength={7} type="tel" placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <input data-testid="audit-form-business-input" required minLength={2} placeholder="Business name" value={form.business} onChange={set("business")} className={inputCls} />
                  <input data-testid="audit-form-url-input" type="url" placeholder="Current website (optional)" value={form.website} onChange={set("website")} className={inputCls} />
                  <label className="flex items-start gap-3 text-[11px] text-[#9CA3AF] leading-relaxed cursor-pointer select-none" data-testid="audit-form-consent-label">
                    <input data-testid="audit-form-consent-checkbox" required type="checkbox" className="mt-0.5 w-4 h-4 shrink-0 rounded border-[#272B35] accent-[#D97706]" />
                    <span>I consent to receiving my personalized video audit and follow-up communication from Rocky Mountain Websites.</span>
                  </label>
                  <button data-testid="audit-form-submit-button" type="submit" disabled={submitting}
                    className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#EA580C] hover:opacity-90 disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-opacity duration-200">
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
                    {submitting ? "Sending..." : "Send me my free 2-min video audit"}
                    {!submitting && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  </button>
                  <p className="text-[11px] text-[#6B7280] text-center">Your details stay with us. No spam, ever.</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#272B35] pt-16 pb-10 overflow-hidden" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="font-display font-bold text-xl text-[#FDFBF7] mb-2" data-testid="footer-brand-heading">Rocky Mountain Websites</p>
            <p className="text-sm text-[#9CA3AF] max-w-sm leading-relaxed">
              Mobile-first websites for local businesses. Designed, built, and cared for by one person you'll actually know.
            </p>
          </div>
          <div className="flex flex-col gap-2.5 text-sm">
            <a data-testid="footer-contact-email" href="mailto:thisisjohnjones@gmail.com" className="flex items-center gap-2.5 text-[#9CA3AF] hover:text-[#FDFBF7] transition-colors duration-200">
              <Mail size={15} className="text-[#F59E0B]" /> thisisjohnjones@gmail.com
            </a>
            <a data-testid="footer-contact-phone" href="tel:+12145004192" className="flex items-center gap-2.5 text-[#9CA3AF] hover:text-[#FDFBF7] transition-colors duration-200">
              <Phone size={15} className="text-[#F59E0B]" /> 214-500-4192
            </a>
          </div>
        </div>
        <p className="font-display font-extrabold text-[11vw] leading-none text-[#1C1F26] select-none whitespace-nowrap" aria-hidden="true">
          ROCKY MOUNTAIN
        </p>
        <p className="text-xs text-[#6B7280] mt-8">© 2026 Rocky Mountain Websites · rockymountainwebsites.com</p>
      </div>
    </footer>
  );
}

export default function AgencyPage() {
  useEffect(() => {
    document.title = "Rocky Mountain Websites — Websites for Local Businesses";
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__rmw_lenis = lenis;
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); lenis.destroy(); window.__rmw_lenis = undefined; };
  }, []);

  useEffect(() => {
    window.__rmw_track = (event) =>
      fetch(`${API}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: window.location.pathname, referrer: document.referrer, event }),
      }).catch(() => {});
    if (sessionStorage.getItem("rmw_tracked")) return;
    sessionStorage.setItem("rmw_tracked", "1");
    window.__rmw_track("visit");
  }, []);

  return (
    <main className="min-h-screen bg-[#0B0C0E] text-[#D1D5DB] overflow-x-clip" data-testid="agency-page">
      <Header />
      <Hero />
      <Problem />
      <Services />
      <Pricing />
      <CaseStudy />
      <Process />
      <AuditForm />
      <Footer />
    </main>
  );
}
